/**
 * Upload History Page
 *
 * Displays a table of all files uploaded by the customer across all of their
 * assigned projects, with file name, size, project, status, and upload date.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload } from 'lucide-react';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  type Column,
} from '@connor-music/shared';
import { customerApiClient } from '@/services/api';
import type { FileUpload, Project } from '@shared/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Row type (FileUpload augmented with project name)
// ---------------------------------------------------------------------------

interface UploadRow extends Record<string, unknown> {
  id: string;
  file_name: string;
  file_size: number;
  project_id: string;
  project_name: string;
  status: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UploadHistory() {
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const projects: Project[] = await customerApiClient.getMyProjects();

        // Fetch uploads for each project in parallel
        const uploadsPerProject = await Promise.all(
          projects.map(async (project) => {
            try {
              const uploads: FileUpload[] = await customerApiClient.getMyUploads(
                project.id,
              );
              return uploads.map((u) => ({
                ...u,
                project_name: project.name,
              }));
            } catch {
              return [] as (FileUpload & { project_name: string })[];
            }
          }),
        );

        if (cancelled) return;

        // Flatten and map to row type
        const allUploads: UploadRow[] = uploadsPerProject
          .flat()
          .map((u) => ({
            id: u.id,
            file_name: u.file_name,
            file_size: u.file_size,
            project_id: u.project_id,
            project_name: u.project_name,
            status: u.status,
            created_at: u.created_at,
          }))
          // Most recent first
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

        setRows(allUploads);
      } catch (err) {
        console.error('Failed to load upload history:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------

  const columns: Column<UploadRow>[] = [
    {
      key: 'file_name',
      label: 'File Name',
      sortable: true,
      render: (_value, item) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {item.file_name as string}
        </span>
      ),
    },
    {
      key: 'file_size',
      label: 'Size',
      sortable: true,
      render: (_value, item) => formatFileSize(item.file_size as number),
    },
    {
      key: 'project_name',
      label: 'Project',
      sortable: true,
      render: (_value, item) => (
        <Link
          to={`/projects/${item.project_id}/upload`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {item.project_name as string}
        </Link>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_value, item) => (
        <StatusBadge status={item.status as string} type="upload" />
      ),
    },
    {
      key: 'created_at',
      label: 'Uploaded',
      sortable: true,
      render: (_value, item) => formatDate(item.created_at as string),
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Upload History
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          View your file upload history across all projects.
        </p>
      </div>

      {!isLoading && rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <EmptyState
            icon={<Upload className="h-12 w-12" />}
            title="No uploads yet"
            description="Files you upload to your projects will appear here."
          />
        </div>
      ) : (
        <DataTable<UploadRow>
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No uploads found."
        />
      )}
    </div>
  );
}
